import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { seo } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Aurora } from "@/components/Aurora";
import { CtaButton } from "@/components/CtaButton";
import { PagePlaceholder } from "@/components/site/PagePlaceholder";
import { getCaseStudyBySlug } from "@/lib/content";

export const Route = createFileRoute("/_site/results/$slug")({
  loader: async ({ params }) => {
    const study = await getCaseStudyBySlug({ data: { slug: params.slug } });
    if (!study) throw notFound();
    return { study };
  },
  head: ({ loaderData, params }) =>
    seo({
      title: loaderData?.study.outcome_headline ?? "Case Study",
      description:
        loaderData?.study.summary ??
        "An engagement case study from LAA Concierge Consulting.",
      path: `/results/${params.slug}`,
    }),
  component: Page,

  errorComponent: () => (
    <PagePlaceholder title="This case study didn't load" intro="Try refreshing the page." />
  ),
  notFoundComponent: () => (
    <PagePlaceholder
      title="Case study not found"
      intro="This case study doesn't exist or was moved."
    />
  ),
});

function Block({ title, body }: { title: string; body: string | null }) {
  if (!body) return null;
  return (
    <div className="mt-10">
      <h2 className="font-display text-h3 text-ink-900">{title}</h2>
      <p className="mt-3 max-w-[68ch] whitespace-pre-line text-body text-ink-600">{body}</p>
    </div>
  );
}

function Page() {
  const { study } = Route.useLoaderData();

  return (
    <>
      <Section tone="paper" className="relative overflow-hidden">
        <Aurora variant="light" className="opacity-70" />
        <Container variant="narrow" className="relative">
          {study.industry && <Eyebrow>{study.industry}</Eyebrow>}
          <h1 className="mt-3 font-display text-display-lg text-ink-900">
            {study.outcome_headline}
          </h1>
          {study.summary && (
            <p className="mt-6 max-w-[68ch] text-body-lg text-ink-600">{study.summary}</p>
          )}

          {(study.metric_1_value || study.metric_2_value) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {study.metric_1_value && (
                <span className="tabular rounded-md bg-brand-100 px-3 py-1.5 text-body-sm text-brand-800">
                  {study.metric_1_value}
                  {study.metric_1_label ? ` ${study.metric_1_label}` : ""}
                </span>
              )}
              {study.metric_2_value && (
                <span className="tabular rounded-md bg-brand-100 px-3 py-1.5 text-body-sm text-brand-800">
                  {study.metric_2_value}
                  {study.metric_2_label ? ` ${study.metric_2_label}` : ""}
                </span>
              )}
            </div>
          )}
        </Container>
      </Section>

      <Section tone="raised">
        <Container variant="narrow">
          <Block title="The situation" body={study.situation} />
          <Block title="What we did" body={study.what_we_did} />
          <Block title="What happened" body={study.what_happened} />

          {study.client_quote && (
            <figure className="mt-12 border-l-2 border-brand-600 pl-6">
              <blockquote className="max-w-[68ch] text-body-lg text-ink-900">
                {study.client_quote}
              </blockquote>
              {study.client_quote_attribution && (
                <figcaption className="mt-3 text-body-sm text-ink-600">
                  {study.client_quote_attribution}
                </figcaption>
              )}
            </figure>
          )}
        </Container>
      </Section>

      <Section tone="dark">
        <Container variant="narrow">
          <div className="relative overflow-hidden rounded-3xl bg-ink-900 p-8 sm:p-12">
            <Aurora variant="dark" />
            <div className="relative">
              <Eyebrow className="text-brand-300">Next step</Eyebrow>
              <h2 className="mt-3 font-display text-h2 text-paper-0" style={{ textWrap: "balance" }}>
                Want a result like this?
              </h2>
              <div className="mt-6">
                <CtaButton asChild tone="dark">
                  <Link to="/book">Book a strategy call</Link>
                </CtaButton>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
